import { Modal } from "@asurion-core/aui-react-modal";
import { Action, Dropdown, Icon, Link, PasswordField, Skeleton } from "@asurion-core/mx-asurion-ui-react";
import { getActionState } from "@ubif/components";
import {
  selectShowPinModal,
  setShowPinModal,
  showToast,
  useGetPinUsersQuery,
  useGlobalDispatch,
  useLazyGetUserQuery,
  usePinLoginMutation
} from "@ubif/util-store";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";

const StyledFooter = styled(Modal.Footer)`
  padding-top: 1.5rem;
  border-top: 1px solid #d5d6da;
`;
/* Applying this style directly on */
const FullWidthPasswordFieldContent = styled(Modal.Content)`
  & div[data-aui="text-field"] {
    width: 100%;
  }
`;

/* Hide the close button as per design, not supported by AUI */
const StyledModal = styled(Modal)`
  & > button {
    display: none;
    visibility: hidden;
  }
`;

export function UserPinModal() {
  const { t } = useTranslation("root-config");
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useGlobalDispatch();
  const showPinModal = useSelector(selectShowPinModal);
  const { isLoading, data } = useGetPinUsersQuery(undefined, { skip: !showPinModal });
  const [attemptPinLogin, { isLoading: isSubmitting, error: pinError }] = usePinLoginMutation();
  const [getUser, { isLoading: getUserLoading }] = useLazyGetUserQuery();
  const [session, setSession] = useState<string>();
  const [pin, setPin] = useState<string>("");
  const pinLengthValid = pin && pin.length === 6;

  useEffect(() => {
    if (showPinModal && data) {
      setSession(Object.values(data).find((user) => user.last)?.sessionId);
    }
  }, [data]);

  useEffect(() => {
    if (modalRef.current) {
      /** this is a hack way to blur the modal backdrop as is not supported by AUI and is outside of the Modal component */
      modalRef.current.parentElement.style.backdropFilter = "blur(10px)";
    }
  }, [modalRef.current]);

  const onSubmit = () => {
    if (!pinLengthValid) return;

    void attemptPinLogin({ sessionId: session, pin })
      .unwrap()
      .then((result) => {
        if (result?.success) {
          setPin(undefined);
          getUser().then((result) => {
            if (result.data.user) {
              dispatch(
                showToast({
                  message: t("PinLoginSuccess", {
                    user: `${result.data.user.givenName} ${result.data.user.familyName}`
                  }),
                  toastType: "default",
                  time: 3000
                })
              );
              dispatch(setShowPinModal(false));
            }
          });
        }
      });
  };

  let content = <Skeleton elementCount={2} height="3rem" />;

  if (!getUserLoading && !isLoading && data) {
    const options = Object.keys(data)
      .map((sessionId) => ({ name: data[sessionId]?.fullName, value: sessionId }))
      .sort((a, b) => a.name.localeCompare(b.name));

    let passwordHelperText: string | undefined;
    if (pinError) {
      const hasPinLockoutError = "data" in pinError && pinError.data?.["pinLockout"] === true;
      passwordHelperText = hasPinLockoutError ? t("PinLockoutError") : t("InvalidPin");
    }

    content = (
      <>
        <Dropdown
          label={t("EmployeeLabel")}
          helperText={t("EmployeeInputHelpText")}
          options={options}
          value={session}
          onChange={(ev: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => setSession(ev.target.value)}
        />
        <PasswordField
          affixLeft={<Icon src="locks/lock-1" size="medium" />}
          label={t("PinInputLabel")}
          value={pin}
          onChange={(ev) => setPin(ev.target.value)}
          fieldStatus={pinError ? "error" : "default"}
          helperText={passwordHelperText}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") onSubmit();
          }}
          disabled={!session}
        />
      </>
    );
  }

  return (
    <StyledModal ref={modalRef} isOpen={showPinModal} isSticky ariaLabel={t("EmployeeLogin")} onCloseModal={() => {}}>
      <Modal.Header>
        <Modal.Title>{t("EmployeeLogin")}</Modal.Title>
        <Modal.Subtitle>{t("EmployeeLoginModalSubtitle")}</Modal.Subtitle>
      </Modal.Header>
      <FullWidthPasswordFieldContent>{content}</FullWidthPasswordFieldContent>
      <StyledFooter>
        <Action
          state={getActionState(isLoading || isSubmitting, !session || !pin || !pinLengthValid)}
          onClick={() => onSubmit()}
        >
          {t("Continue")}
        </Action>
        <Link
          style={{ height: "3rem" }}
          href="/api/auth/signout"
          btnVariantProps={{ btnVariant: "outline", btnSize: "large" }}
          variant="button"
          disabled={isSubmitting}
        >
          {t("Logout")}
        </Link>
      </StyledFooter>
    </StyledModal>
  );
}
